import { CheckCircle2, DollarSign, MessageSquare, Play, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LocalDateTime } from "@/components/ui/local-datetime";
import { cn } from "@/lib/utils";

export type RevisionStatus =
  | "OPEN"
  | "AWAITING_CUSTOMER"
  | "APPROVED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "RESOLVED"
  | "DECLINED";

export type RevisionRequestRow = {
  id: string;
  title: string;
  message: string;
  status: RevisionStatus;
  scope: string;
  area: string | null;
  adminDecision: string;
  quotedAmountUsd: number | null;
  paymentStatus: string;
  customerDecision: string | null;
  createdAt: string; // ISO string
  resolvedAt: string | null; // ISO string
};

export function RevisionRequests({
  orderId,
  requests,
  repliesByRequestId,
  etaByRequestId,
}: {
  orderId: string;
  requests: RevisionRequestRow[];
  repliesByRequestId: Record<string, { message: string; createdAt: string }>;
  etaByRequestId: Record<string, { hours: number; startedAt: string; targetAt: string }>;
}) {
  const safeRequests = Array.isArray(requests) ? requests : [];

  if (safeRequests.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground">
          Revision requests
        </h2>
        <Card className="border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
          No revision requests for this order yet.
        </Card>
      </section>
    );
  }

  const statusPriority: Record<RevisionStatus, number> = {
    IN_PROGRESS: 0,
    APPROVED: 1,
    AWAITING_CUSTOMER: 2,
    OPEN: 3,
    COMPLETED: 4,
    RESOLVED: 5,
    DECLINED: 6,
  };
  const sorted = safeRequests.slice().sort((a, b) => {
    const prio = statusPriority[a.status] - statusPriority[b.status];
    if (prio !== 0) return prio;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-muted-foreground">
        Revision requests
      </h2>
      <div className="flex flex-col gap-3">
        {sorted.map((r) => (
          <RequestRow
            key={r.id}
            orderId={orderId}
            request={r}
            reply={repliesByRequestId[r.id] ?? null}
            eta={etaByRequestId[r.id] ?? null}
          />
        ))}
      </div>
    </section>
  );
}

function RequestRow({
  orderId,
  request,
  reply,
  eta,
}: {
  orderId: string;
  request: RevisionRequestRow;
  reply: { message: string; createdAt: string } | null;
  eta: { hours: number; startedAt: string; targetAt: string } | null;
}) {
  const canTriage = request.status === "OPEN";
  const canStart =
    request.status === "APPROVED" &&
    (request.adminDecision === "FREE" || request.paymentStatus === "PAID");
  const canClose = request.status === "IN_PROGRESS";
  const quoteAcceptedAwaitingPayment =
    request.adminDecision === "PAID" &&
    request.customerDecision === "ACCEPTED" &&
    request.paymentStatus === "PENDING";
  const statusLabel = quoteAcceptedAwaitingPayment
    ? "QUOTE ACCEPTED · AWAITING PAYMENT"
    : request.status.replace(/_/g, " ");
  const paymentText =
    request.paymentStatus === "PAID"
      ? "paid"
      : request.paymentStatus === "PENDING"
        ? request.customerDecision === "ACCEPTED"
          ? "awaiting payment after quote acceptance"
          : "awaiting customer decision"
        : "not required";
  return (
    <Card
      className={cn(
        "space-y-3 p-4",
        !(canTriage || canStart || canClose) && "border-dashed bg-muted/30 opacity-90",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <StatusBadge status={request.status} overrideLabel={statusLabel} />
        <span className="text-[11px] text-muted-foreground">
          <LocalDateTime iso={request.createdAt} preset="medium" />
          {request.resolvedAt ? (
            <>
              {" "}
              · resolved{" "}
              <LocalDateTime iso={request.resolvedAt} preset="medium" />
            </>
          ) : null}
        </span>
      </div>
      <p className="text-sm font-medium text-foreground">{request.title}</p>
      <div className="flex flex-wrap gap-2 text-[11.5px] text-muted-foreground">
        <span className="rounded-full border px-2 py-0.5">{request.scope.toLowerCase()}</span>
        {request.area ? (
          <span className="rounded-full border px-2 py-0.5">{request.area}</span>
        ) : null}
        <span className="rounded-full border px-2 py-0.5">
          decision: {request.adminDecision.toLowerCase()}
        </span>
        <span className="rounded-full border px-2 py-0.5">payment: {paymentText}</span>
        {request.quotedAmountUsd ? (
          <span className="rounded-full border px-2 py-0.5">
            quote: ${(request.quotedAmountUsd / 100).toFixed(2)}
          </span>
        ) : null}
      </div>
      <p className="whitespace-pre-wrap text-sm text-foreground/90">
        {request.message}
      </p>
      {eta ? (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <p>
            ETA: <LocalDateTime iso={eta.targetAt} preset="dateTime" /> ({eta.hours}h)
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground">
            Started <LocalDateTime iso={eta.startedAt} preset="medium" />
          </p>
        </div>
      ) : null}
      {reply ? (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <p className="font-medium text-foreground">Latest reply sent to customer</p>
          <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
            {reply.message}
          </p>
          <p className="mt-2 text-[11px] text-muted-foreground">
            <LocalDateTime iso={reply.createdAt} preset="medium" />
          </p>
        </div>
      ) : null}
      {canTriage ? (
        <div className="flex flex-wrap gap-2">
          <ActionDialog orderId={orderId} requestId={request.id} action="DECIDE_FREE" />
          <ActionDialog orderId={orderId} requestId={request.id} action="QUOTE_PAID" />
          <ActionDialog orderId={orderId} requestId={request.id} action="DECLINE" />
        </div>
      ) : null}
      {canStart ? (
        <div className="flex flex-wrap gap-2">
          <ActionDialog orderId={orderId} requestId={request.id} action="START_WORK" />
        </div>
      ) : null}
      {canClose ? (
        <div className="flex flex-wrap gap-2">
          <ActionDialog orderId={orderId} requestId={request.id} action="MARK_DONE" />
        </div>
      ) : null}
    </Card>
  );
}

function StatusBadge({
  status,
  overrideLabel,
}: {
  status: RevisionStatus;
  overrideLabel?: string;
}) {
  const tone = {
    OPEN: "bg-amber-500/10 text-amber-700 ring-amber-500/20",
    AWAITING_CUSTOMER: "bg-blue-500/10 text-blue-700 ring-blue-500/20",
    APPROVED: "bg-violet-500/10 text-violet-700 ring-violet-500/20",
    IN_PROGRESS: "bg-orange-500/10 text-orange-700 ring-orange-500/20",
    COMPLETED: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
    RESOLVED: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
    DECLINED: "bg-muted text-muted-foreground ring-border",
  }[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.06em] ring-1 ring-inset",
        tone,
      )}
    >
      {overrideLabel ?? status.replace(/_/g, " ")}
    </span>
  );
}

function ActionDialog({
  orderId,
  requestId,
  action,
}: {
  orderId: string;
  requestId: string;
  action: "DECIDE_FREE" | "QUOTE_PAID" | "DECLINE" | "START_WORK" | "MARK_DONE";
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [quoteAmount, setQuoteAmount] = useState("");
  const [etaHours, setEtaHours] = useState("");

  async function onConfirm() {
    setBusy(true);
    try {
      const quoteAmountUsd =
        action === "QUOTE_PAID" ? Math.round(Number(quoteAmount) * 100) : undefined;
      const res = await fetch(
        `/api/admin/orders/${orderId}/revision-request/${requestId}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action,
            ...(quoteAmountUsd ? { quoteAmountUsd } : {}),
            ...(action === "START_WORK" && etaHours.trim()
              ? { etaHours: Number(etaHours) }
              : {}),
            ...(reply.trim() ? { reply: reply.trim() } : {}),
          }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        toast.error(body.error ?? "Couldn't update");
        return;
      }
      toast.success("Revision request updated.");
      setOpen(false);
      setReply("");
      setQuoteAmount("");
      setEtaHours("");
      navigate(0);
    } catch {
      toast.error("Network error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button
            variant={action === "DECLINE" ? "outline" : "default"}
            size="sm"
            type="button"
          >
            {action === "DECIDE_FREE" ? <CheckCircle2 className="size-3.5" /> : null}
            {action === "QUOTE_PAID" ? <DollarSign className="size-3.5" /> : null}
            {action === "DECLINE" ? <XCircle className="size-3.5" /> : null}
            {action === "START_WORK" ? <Play className="size-3.5" /> : null}
            {action === "MARK_DONE" ? <CheckCircle2 className="size-3.5" /> : null}
            {action === "DECIDE_FREE"
              ? "Approve free"
              : action === "QUOTE_PAID"
                ? "Quote paid"
                : action === "DECLINE"
                  ? "Decline"
                  : action === "START_WORK"
                    ? "Start work"
                    : "Mark done"}
          </Button>
        }
      />
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {action === "DECIDE_FREE"
              ? "Approve as free revision?"
              : action === "QUOTE_PAID"
                ? "Set paid revision quote?"
                : action === "DECLINE"
                  ? "Decline this revision request?"
                  : action === "START_WORK"
                    ? "Start revision work?"
                    : "Mark revision done?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {action === "QUOTE_PAID"
              ? "Customer will be asked to accept/decline this quote."
              : action === "START_WORK"
                ? "This moves the order to Revisions Pending."
                : "Optionally send a note to the customer in their Build Room."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {action === "QUOTE_PAID" ? (
          <div className="space-y-1.5">
            <Label htmlFor={`quote-${requestId}`}>Quote amount (USD)</Label>
            <input
              id={`quote-${requestId}`}
              type="number"
              min="1"
              step="0.01"
              value={quoteAmount}
              onChange={(e) => setQuoteAmount(e.target.value)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              placeholder="e.g. 149.00"
            />
          </div>
        ) : null}
        {action === "START_WORK" ? (
          <div className="space-y-1.5">
            <Label htmlFor={`eta-${requestId}`}>Estimated hours to complete</Label>
            <input
              id={`eta-${requestId}`}
              type="number"
              min="1"
              max="336"
              step="1"
              value={etaHours}
              onChange={(e) => setEtaHours(e.target.value)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              placeholder="e.g. 24"
            />
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor={`reply-${requestId}-${action}`}>
            <MessageSquare className="mr-1 inline size-3.5" />
            Reply (optional)
          </Label>
          <Textarea
            id={`reply-${requestId}-${action}`}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Markdown supported. Customer sees this in their Build Room and gets an email."
            rows={4}
            disabled={busy}
            maxLength={8000}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={
              busy ||
              (action === "START_WORK" &&
                (!etaHours.trim() || Number.isNaN(Number(etaHours)) || Number(etaHours) <= 0))
            }
          >
            {busy ? "Saving..." : "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
